// ./src/config/bodyTypes.ts
// Body type configuration for vehicles
// Body types come from car_body_types database table
// This file contains ID constants and vehicle type detection logic

// ============================================
// Types
// ============================================

export type VehicleType = "car" | "motorcycle" | "truck" | "other";

// ============================================
// Body Type IDs (from car_body_types table)
// ============================================

export const BODY_TYPE_IDS = {
  // Cars (default category)
  UNKNOWN: 0,
  SEDAN: 1,
  HATCHBACK: 2,
  SUV: 3,
  CROSSOVER: 4,
  WAGON: 10,
  COUPE: 11,
  CONVERTIBLE: 12,
  SPORT: 13,
  LIMOUSINE: 14,

  // Trucks / Commercial vehicles
  TRUCK: 5,
  PICKUP: 6,
  MICROVAN: 7,
  MINIVAN: 8,
  VAN: 9,
  RV: 15,
  MINIBUS: 16,
  BUS: 17,
  FREIGHT: 18,

  // Motorcycles
  MOTORCYCLE: 100,
  SCOOTER: 101,

  // Other
  OTHER: 1000,
} as const;

// ============================================
// Vehicle Type Detection Sets
// ============================================

/** Body type IDs that represent motorcycles */
export const MOTORCYCLE_BODY_TYPE_IDS: ReadonlySet<number> = new Set([
  BODY_TYPE_IDS.MOTORCYCLE,
  BODY_TYPE_IDS.SCOOTER,
]);

/** Body type IDs that represent trucks/commercial vehicles */
export const TRUCK_BODY_TYPE_IDS: ReadonlySet<number> = new Set([
  BODY_TYPE_IDS.TRUCK,
  BODY_TYPE_IDS.PICKUP,
  BODY_TYPE_IDS.MICROVAN,
  BODY_TYPE_IDS.MINIVAN,
  BODY_TYPE_IDS.VAN,
  BODY_TYPE_IDS.RV,
  BODY_TYPE_IDS.MINIBUS,
  BODY_TYPE_IDS.BUS,
  BODY_TYPE_IDS.FREIGHT,
]);

/** Body type IDs that represent standard cars */
export const CAR_BODY_TYPE_IDS: ReadonlySet<number> = new Set([
  BODY_TYPE_IDS.UNKNOWN,
  BODY_TYPE_IDS.SEDAN,
  BODY_TYPE_IDS.HATCHBACK,
  BODY_TYPE_IDS.SUV,
  BODY_TYPE_IDS.CROSSOVER,
  BODY_TYPE_IDS.WAGON,
  BODY_TYPE_IDS.COUPE,
  BODY_TYPE_IDS.CONVERTIBLE,
  BODY_TYPE_IDS.SPORT,
  BODY_TYPE_IDS.LIMOUSINE,
]);

// ============================================
// Helper Functions
// ============================================

/**
 * Get vehicle type from body type ID
 * @param bodyTypeId - Body type ID from the database
 * @returns Vehicle type category
 */
export const getVehicleTypeFromBodyTypeId = (
  bodyTypeId: string | number | null | undefined
): VehicleType => {
  if (bodyTypeId === null || bodyTypeId === undefined) return "car";

  const id = typeof bodyTypeId === "string" ? parseInt(bodyTypeId, 10) : bodyTypeId;

  if (isNaN(id)) return "car";

  if (MOTORCYCLE_BODY_TYPE_IDS.has(id)) {
    return "motorcycle";
  }

  if (TRUCK_BODY_TYPE_IDS.has(id)) {
    return "truck";
  }

  if (id === BODY_TYPE_IDS.OTHER) {
    return "other";
  }

  return "car";
};

/**
 * Check if body type ID represents a motorcycle
 * @param bodyTypeId - Body type ID
 * @returns True if motorcycle
 */
export const isMotorcycleBodyType = (
  bodyTypeId: string | number | null | undefined
): boolean => {
  return getVehicleTypeFromBodyTypeId(bodyTypeId) === "motorcycle";
};

/**
 * Check if body type ID represents a truck/commercial vehicle
 * @param bodyTypeId - Body type ID
 * @returns True if truck
 */
export const isTruckBodyType = (
  bodyTypeId: string | number | null | undefined
): boolean => {
  return getVehicleTypeFromBodyTypeId(bodyTypeId) === "truck";
};

/**
 * Check if body type ID represents a standard car
 * @param bodyTypeId - Body type ID
 * @returns True if car
 */
export const isCarBodyType = (
  bodyTypeId: string | number | null | undefined
): boolean => {
  return getVehicleTypeFromBodyTypeId(bodyTypeId) === "car";
};